import { Inject, Options, Prop, Ref, Vue } from 'vue-property-decorator';
import ModalBox from '@/shared/components/modal-box/index.vue';
import FileDragAndDrop from '@/shared/components/file-drag-and-drop/index.vue';
import { TrustTIAPI } from '@/shared/modules/trust-ti-api';
import { TrustAPI } from '@/shared/modules/trust-api';
import { AppInsights } from '@/trust-investigator-app/appInsights';
import storeTI from '@/trust-investigator-app/store';
import storeTA from '@/trust-admin-app/store';
import { EventEmitter } from 'events';
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import {
  AdditionalFile,
  ComplianceFormFileRequest,
  InvestigatorWorkingPaperView,
} from '@/shared/modules/typescript-trustapi-client/models';
import FilePreview from '@/shared/components/file-preview/index.vue';
import { FilePreviewData } from '@/shared/components/file-preview/file-preview-data';
import ErrorIndicator from '@/shared/components/error-indicator/index.vue';
import LoadingIndicator from '../loading-indicator/index.vue';
import AlertBox from '../alert-box/index.vue';
import { RoleType } from '@/trust-admin-app/models/user';
import { AuthorizedUser } from '@/trust-admin-app/models/authorized-user';
import AccessValidationMixin from '@/shared/mixins/access-validation';

@Options({
  components: {
    ModalBox,
    FileDragAndDrop,
    ErrorIndicator,
    LoadingIndicator,
    FilePreview,
    AlertBox,
  },
  mixins: [FormatDateTimeMixin, AccessValidationMixin],
})
export default class AdditionalDocumentsModal extends Vue {
  @Inject({ from: 'apiService', default: new TrustTIAPI() })
  private apiServiceTI!: TrustTIAPI;

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiServiceTA!: TrustAPI;

  @Ref('fileDropAndDrop') readonly fileDropAndDrop!: any;

  @Prop({ default: null })
  private practice!: any;

  @Prop({ default: 'TA' })
  private appName!: string;

  private isSubmitting = false;
  private showFileEditModal = false;

  private acceptAnyFileTypes = true;

  private showFilePreview = false;

  private selectedWorkingPaper: InvestigatorWorkingPaperView = null as any;

  private selectedFileForPreview: FilePreviewData = new FilePreviewData();

  private files: AdditionalFile[] = [];

  private readOnly = false;

  private filePreviewState = 1;
  private isLoadingStream = false;
  private isLoading = false;

  created(): void {
    this.getFileList();
  }

  private openWorkingPaperEditor(f: InvestigatorWorkingPaperView): void {
    this.selectedWorkingPaper = f;
    this.showFileEditModal = true;
  }

  private closeWorkingPaperEditor(): void {
    this.selectedWorkingPaper = null as any;
    this.showFileEditModal = false;
  }

  private getFileList(): void {
    this.isLoading = true;
    const _complianceFormId: number = this.practice.id;
    if (this.appName === 'TA') {
      this.apiServiceTA.ComplianceFormApi?.apiV2ComplianceFormAdminGetComplianceFormAdditionalFilesComplianceFormIdGet(
        _complianceFormId,
      )
        .then((res) => {
          this.files = res.data.data;
          // sort by createdOn desc
          this.files.sort((a, b) => {
            return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();
          });
          this.isLoading = false;
        })
        .catch((err) => {
          this.isLoading = false;
          // this.ai.trackException('Error on getting working papers: ' + JSON.stringify(err));
          this.eventCtrl.emit('show-snackbar', 'error', 'Error on getting working papers: ' + err);
        });
    } else {
      this.apiServiceTI.ComplianceFormApiV2?.apiV2ComplianceFormGetComplianceFormAdditionalFileComplianceFormIdGet(
        _complianceFormId,
      )
        .then((res) => {
          this.files = res.data.data;
          // sort by createdOn desc
          this.isLoading = false;
          this.files.sort((a, b) => {
            return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();
          });
        })
        .catch((err) => {
          this.isLoading = false;
          // this.ai.trackException('Error on getting working papers: ' + JSON.stringify(err));
          this.eventCtrl.emit('show-snackbar', 'error', 'Error on getting working papers: ' + err);
        });
    }
  }

  private async getFileStream(file: AdditionalFile): Promise<void> {
    this.isLoadingStream = true;
    try {
      this.filePreviewState = 1;
      let resp: any;
      if (this.appName === 'TA') {
        resp = await this.apiServiceTA.FileAtcsApi?.apiV2FileAtcsAdminDownloadStreamFileIdGet(file.fileId, {
          responseType: 'blob',
        });
      } else {
        const _complianceFormId: number = this.practice.id;
        resp =
          await this.apiServiceTI.ComplianceFormApiV2?.apiV2ComplianceFormDownloadAdditionalFileStreamComplianceFormIdFileIdGet(
            _complianceFormId,
            file.fileId,
            {
              responseType: 'blob',
            },
          );
      }

      this.selectedFileForPreview = new FilePreviewData(
        file.fileName,
        file.fileExtension,
        resp.data,
        resp.headers['content-type'],
      );
      this.openFilePreviewer(this.selectedFileForPreview);
      this.isLoadingStream = false;
      this.filePreviewState = 2;
    } catch (error) {
      this.isLoadingStream = false;
      this.filePreviewState = 3;
      this.eventCtrl.emit('show-snackbar', 'error', 'Error on getting file stream: ' + error);
      console.log(error);
    }
  }

  private openFilePreviewer(file: FilePreviewData): void {
    this.selectedFileForPreview = file;
    this.showFilePreview = true;
  }

  private async uploadAllFiles(fileList: Array<File>): Promise<void> {
    const allRequests: Array<Promise<boolean>> = [];
    this.isSubmitting = true;
    try {
      if (fileList.length > 0) {
        fileList.forEach((x) => allRequests.push(this.uploadFile(x)));
        const resp: any = await Promise.all(allRequests);
        this.fileDropAndDrop.selectedFiles = [];

        const failedUpload = resp.filter((x: any) => x.err).map((x: any) => x.file.name);

        if (failedUpload.length > 0) {
          const filesInString = failedUpload.join('<br />');
          this.eventCtrl.emit('show-snackbar', 'error', 'File(s) failed to upload:<br />' + filesInString);
        } else {
          this.eventCtrl.emit('show-snackbar', 'success', 'File(s) uploaded');
        }
        this.getFileList();
      }
    } catch (err: any) {
      console.log(err);
      // this.ai.trackException('Error on upload file: ' + JSON.stringify(err));
      this.eventCtrl.emit('show-snackbar', 'error', 'Error on upload file: ' + err);
    } finally {
      this.isSubmitting = false;
    }
  }

  private async uploadFile(file: File): Promise<any> {
    const _id: number = this.practice.id;
    try {
      const body: ComplianceFormFileRequest = {
        complianceFormId: _id,
        fileName: file.name,
        fileDataUrl: '',
        fileExtension: file.name.split('.').pop() as string,
      };

      const fileStr = await this.fileToString(file);
      body.fileDataUrl = fileStr;
      await this.apiServiceTA.FileAtcsApi?.apiV2FileAtcsAdminSaveComplianceFormAdditionalFilePost(body);
      return Promise.resolve({ err: false, file });
    } catch (err) {
      console.log(err);
      return Promise.reject({ err: true, file });
    }
  }

  private fileToString(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        return resolve(reader.result as string);
      };

      reader.onabort = () => {
        return reject();
      };

      reader.onerror = () => {
        return reject();
      };
    });
  }

  get eventCtrl(): EventEmitter {
    return this.appName === 'TA' ? storeTA.getters.getEventController : storeTI.getters.getEventController;
  }

  private closeModal() {
    this.$emit('reloadData');
    this.$emit('close');
  }

  private closeFilePreview() {
    this.showFilePreview = false;
  }

  private showDeleteConfirmation = false;
  private fileToDelete = null as any;

  private async deleteFile(file: AdditionalFile): Promise<void> {
    this.fileToDelete = file;
    this.showDeleteConfirmation = true;
  }

  private async deleteIt() {
    try {
      await this.apiServiceTA.FileAtcsApi?.apiV2FileAtcsAdminDeleteAdditionalFileComplianceFormIdFileIdDelete(
        this.practice.id,
        this.fileToDelete.fileId,
      );
      this.showDeleteConfirmation = false;
      this.getFileList();
    } catch (err) {
      console.log(err);
      this.showDeleteConfirmation = false;
    }
  }

  get usrAcct(): AuthorizedUser {
    return this.appName === 'TA' ? storeTA.getters.getUserAccount : storeTI.getters.getUserAccount;
  }

  get isSuperAdmin(): boolean {
    return this.$accessPermitted(this.usrAcct.user.roles, [RoleType.TrustSuperAdmin]);
  }

  private getViewButtonLabel(f: AdditionalFile): string {
    try {
      if (f.fileExtension?.toLocaleLowerCase() === 'pdf') {
        return 'View';
      } else {
        const fileType: string[] = ['png', 'jpg', 'bmp', 'jpeg'];
        const fileExtension = f.fileExtension?.toLocaleLowerCase() || '';
        return fileType.indexOf(fileExtension) >= 0 ? 'View' : 'Download';
      }
    } catch (e) {
      return 'Download';
    }
  }
}
